import React, { useState, useEffect, useRef } from 'react';
import {
  Users,
  DollarSign,
  TrendingUp,
  Download,
  Search,
  Eye,
  RefreshCw,
  Trash2,
  UserX,
  BarChart3,
  Building,
  CreditCard,
  Percent,
  Settings,
  Key,
  ShoppingCart,
  Banknote,
  Wallet,
  SplitSquareHorizontal,
  FileText
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  getRegistryStats,
  getDashboardStats,
  getUserStats,
  exportRegistryData,
  exportUserData,
  getFeeSettings
} from '../lib/admin';
import VendorManagement from '../components/admin/VendorManagement';
import VenueManagement from '../components/admin/VenueManagement';
import WithdrawalManagement from '../components/admin/WithdrawalManagement';
import DeleteRegistryModal from '../components/DeleteRegistryModal';
import DeleteUserModal from '../components/DeleteUserModal';
import FeeSettingsModal from '../components/FeeSettingsModal';
import ResetUserPasswordModal from '../components/ResetUserPasswordModal';
import InvoiceModal from '../components/InvoiceModal';
import type { RegistryStats, AdminDashboardStats, UserStats } from '../types/admin';

export default function AdminDashboard() {
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [registryStats, setRegistryStats] = useState<RegistryStats[]>([]);
  const [userStats, setUserStats] = useState<UserStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'registries' | 'users' | 'vendors' | 'venues' | 'withdrawals' | 'fees'>('registries');
  const [selectedRegistry, setSelectedRegistry] = useState<RegistryStats | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserStats | null>(null);
  const [selectedUserForReset, setSelectedUserForReset] = useState<UserStats | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFeeSettingsModal, setShowFeeSettingsModal] = useState(false);
  const [invoiceRegistry, setInvoiceRegistry] = useState<RegistryStats | null>(null);
  const [platformFeeRate, setPlatformFeeRate] = useState(0.0131);
  const [gatewayFeeRate, setGatewayFeeRate] = useState(0.0269);
  const sparklesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadData();

    const createSparkle = () => {
      if (!sparklesRef.current) return;
      const sparkle = document.createElement('div');
      sparkle.className = 'sparkle';
      const startX = Math.random() * window.innerWidth;
      const endX = startX + (Math.random() * 100 - 50);
      sparkle.style.setProperty('--start-x', `${startX}px`);
      sparkle.style.setProperty('--end-x', `${endX}px`);
      sparkle.style.left = `${startX}px`;
      sparklesRef.current.appendChild(sparkle);
      setTimeout(() => sparkle.remove(), 3000);
    };

    const interval = setInterval(createSparkle, 200);
    for (let i = 0; i < 10; i++) setTimeout(createSparkle, i * 100);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [dashboardStats, registries, users, feeSettings] = await Promise.all([
        getDashboardStats(),
        getRegistryStats(),
        getUserStats(),
        getFeeSettings()
      ]);

      setStats(dashboardStats);
      setRegistryStats(registries);
      setUserStats(users);
      setPlatformFeeRate(feeSettings.platformFeeRate);
      setGatewayFeeRate(feeSettings.gatewayFeeRate);
    } catch (err) {
      console.error('Error loading admin data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const filteredRegistries = registryStats.filter(registry =>
    registry.owner_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    registry.owner_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    registry.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = userStats.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExport = () => {
    if (activeTab === 'registries') exportRegistryData();
    else if (activeTab === 'users') exportUserData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-pastel-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pastel-purple-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-pastel-gradient flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">{error}</p>
          <button onClick={loadData} className="btn-pastel px-4 py-2">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-pastel-gradient">
      <div ref={sparklesRef} className="sparkles-container" />

      <div className="container mx-auto px-4 py-8">
        <div className="space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 font-poppins">Admin Dashboard</h1>
              <p className="text-gray-600 mt-2">Manage users, registries, and platform commission</p>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/admin/analytics"
                className="flex items-center space-x-2 px-4 py-2 bg-pastel-blue-400 text-white rounded-md hover:bg-pastel-blue-500"
              >
                <BarChart3 className="w-4 h-4" />
                <span>Analytics Dashboard</span>
              </Link>
              <button
                onClick={() => setShowFeeSettingsModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-pastel-green-400 text-white rounded-md hover:bg-pastel-green-500"
              >
                <Settings className="w-4 h-4" />
                <span>Fee Settings</span>
              </button>
              <button
                onClick={loadData}
                className="btn-pastel flex items-center space-x-2 px-4 py-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              <div className="card-pastel p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <Users className="w-5 h-5 text-pastel-blue-600" />
                  <span className="text-sm text-gray-500">Total Registries</span>
                </div>
                <p className="text-2xl font-bold text-gray-800 font-poppins">{stats.total_registries}</p>
              </div>

              <div className="card-pastel p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-pastel-green-600" />
                  <span className="text-sm text-gray-500">Total Collected</span>
                </div>
                <p className="text-2xl font-bold text-gray-800 font-poppins">LKR {(stats.total_collected || 0).toFixed(2)}</p>
              </div>

              <div className="card-pastel p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <ShoppingCart className="w-5 h-5 text-pastel-blue-600" />
                  <span className="text-sm text-gray-500">Transactions</span>
                </div>
                <p className="text-2xl font-bold text-gray-800 font-poppins">{stats.total_transactions}</p>
              </div>

              <div className="card-pastel p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <DollarSign className="w-5 h-5 text-pastel-green-600" />
                  <span className="text-sm text-gray-500">Commission Earned</span>
                </div>
                <p className="text-2xl font-bold text-gray-800 font-poppins">LKR {(stats.total_commission || 0).toFixed(2)}</p>
              </div>

              <div className="card-pastel p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <Wallet className="w-5 h-5 text-amber-500" />
                  <span className="text-sm text-gray-500">Net Transferable</span>
                </div>
                <p className="text-2xl font-bold text-gray-800 font-poppins">LKR {(stats.net_transferable || 0).toFixed(2)}</p>
                <p className="text-xs text-gray-400 mt-1">After commission deduction</p>
              </div>

              <div className="card-pastel p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <Users className="w-5 h-5 text-pastel-purple-600" />
                  <span className="text-sm text-gray-500">Active Users</span>
                </div>
                <p className="text-2xl font-bold text-gray-800 font-poppins">{stats.active_users}</p>
              </div>
            </div>

            {/* Fee Split Breakdown Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="card-pastel p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <DollarSign className="w-5 h-5 text-pastel-blue-600" />
                  <span className="text-sm text-gray-500">Fees Paid by Guests</span>
                </div>
                <p className="text-2xl font-bold text-gray-800 font-poppins">LKR {(stats.total_guest_fee_share || 0).toFixed(2)}</p>
                <p className="text-xs text-gray-400 mt-1">Added on top of gift amount</p>
              </div>

              <div className="card-pastel p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <Wallet className="w-5 h-5 text-orange-500" />
                  <span className="text-sm text-gray-500">Fees Deducted from Couples</span>
                </div>
                <p className="text-2xl font-bold text-gray-800 font-poppins">LKR {(stats.total_couple_fee_share || 0).toFixed(2)}</p>
                <p className="text-xs text-gray-400 mt-1">Subtracted from gift amount</p>
              </div>

              <div className="card-pastel p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <SplitSquareHorizontal className="w-5 h-5 text-pastel-blue-600" />
                  <span className="text-sm text-gray-500">Fee Model Breakdown</span>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Guest Pays:</span>
                    <span className="text-gray-800 font-medium">{stats.registries_guest_pays || 0} <span className="text-gray-400 text-xs">registries</span></span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Couple Absorbs:</span>
                    <span className="text-gray-800 font-medium">{stats.registries_couple_absorbs || 0} <span className="text-gray-400 text-xs">registries</span></span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Split 50/50:</span>
                    <span className="text-gray-800 font-medium">{stats.registries_split || 0} <span className="text-gray-400 text-xs">registries</span></span>
                  </div>
                </div>
              </div>

              <div className="card-pastel p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <BarChart3 className="w-5 h-5 text-pastel-purple-600" />
                  <span className="text-sm text-gray-500">Commission by Fee Model</span>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Guest Pays:</span>
                    <span className="text-pastel-blue-600 font-medium">LKR {(stats.commission_from_guest_pays || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Couple Absorbs:</span>
                    <span className="text-orange-500 font-medium">LKR {(stats.commission_from_couple_absorbs || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Split 50/50:</span>
                    <span className="text-pastel-blue-600 font-medium">LKR {(stats.commission_from_splits || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
            </>
          )}

          {/* Tabs */}
          <div className="flex flex-wrap space-x-1 card-pastel p-1 rounded-lg">
            {(['registries', 'users', 'vendors', 'venues', 'withdrawals', 'fees'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-md transition-colors capitalize ${
                  activeTab === tab ? 'btn-pastel' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {tab === 'registries' ? 'Gift Registries'
                  : tab === 'users' ? 'User Accounts'
                  : tab === 'fees' ? 'Fee Structure'
                  : tab === 'withdrawals' ? 'Withdrawals'
                  : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Search */}
          {!['fees', 'vendors', 'venues', 'withdrawals'].includes(activeTab) && (
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={`Search ${activeTab === 'users' ? 'users' : 'registries'} by name, email, or title...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-pastel pl-10"
                />
              </div>
              <button
                onClick={handleExport}
                className="flex items-center space-x-2 px-4 py-2 bg-white/60 text-gray-800 rounded-md hover:bg-white/80 border border-white/40"
              >
                <Download className="w-4 h-4" />
                <span>Export CSV</span>
              </button>
            </div>
          )}

          {/* Content */}
          {activeTab === 'registries' ? (
            <div className="card-pastel overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-white/60">
                    <tr>
                      <th className="px-6 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Registry Details</th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Owner Information</th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Fee Type</th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Total Collected</th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Created Date</th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-pastel-purple-200">
                    {filteredRegistries.map((registry) => (
                      <tr key={registry.registry_id} className="hover:bg-white/60">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-800">{registry.title}</p>
                            <p className="text-sm text-gray-500">ID: {registry.registry_id}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-800">{registry.owner_name}</p>
                            <p className="text-sm text-gray-500">{registry.owner_email}</p>
                            {registry.contact_number && (
                              <p className="text-sm text-gray-500">{registry.contact_number}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            registry.fee_responsibility === 'GUEST' ? 'bg-pastel-blue-100 text-pastel-blue-600' :
                            registry.fee_responsibility === 'COUPLE' ? 'bg-orange-100 text-orange-600' :
                            registry.fee_responsibility === 'SPLIT' ? 'bg-pastel-green-100 text-pastel-green-600' :
                            'bg-white/60 text-gray-600'
                          }`}>
                            {registry.fee_responsibility === 'GUEST' ? 'Guest Pays' :
                             registry.fee_responsibility === 'COUPLE' ? 'Couple Absorbs' :
                             registry.fee_responsibility === 'SPLIT' ? 'Split 50/50' :
                             registry.fee_responsibility || 'N/A'}
                          </span>
                          {(registry.platform_fee_percentage != null || registry.gateway_fee_percentage != null) && (
                            <p className="text-xs text-gray-400 mt-1">
                              {Number(registry.platform_fee_percentage || 0).toFixed(2)}% + {Number(registry.gateway_fee_percentage || 0).toFixed(2)}%
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-pastel-green-600">LKR {registry.total_collected.toFixed(2)}</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(registry.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <a
                              href={`/registries/${registry.registry_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center space-x-1 px-3 py-1 bg-pastel-blue-400 text-white rounded-md hover:bg-pastel-blue-500 text-sm"
                            >
                              <Eye className="w-4 h-4" />
                              <span>View</span>
                            </a>
                            <button
                              onClick={() => setInvoiceRegistry(registry)}
                              className="flex items-center space-x-1 px-3 py-1 bg-pastel-green-400 text-white rounded-md hover:bg-pastel-green-500 text-sm"
                            >
                              <FileText className="w-4 h-4" />
                              <span>Invoice</span>
                            </button>
                            <button
                              onClick={() => setSelectedRegistry(registry)}
                              className="flex items-center space-x-1 px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-400 text-sm"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : activeTab === 'users' ? (
            <div className="card-pastel overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-white/60">
                    <tr>
                      <th className="px-6 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">User Details</th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Account Info</th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Activity</th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Total Collected</th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-pastel-purple-200">
                    {filteredUsers.map((user) => (
                      <tr key={user.user_id} className="hover:bg-white/60">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-800">{user.email}</p>
                            <p className="text-sm text-gray-500">{user.full_name || 'No name set'}</p>
                            <p className="text-sm text-gray-500">@{user.username || 'No username'}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <p className="text-sm text-gray-800">Joined: {new Date(user.created_at).toLocaleDateString()}</p>
                            <p className="text-sm text-gray-500">
                              Last Sign In: {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : 'Never'}
                            </p>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              user.is_active ? 'bg-pastel-green-100 text-pastel-green-600' : 'bg-red-100 text-red-600'
                            }`}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-800">
                            Registries: <span className="font-medium">{user.registry_count}</span>
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-pastel-green-600">LKR {user.total_collected.toFixed(2)}</p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setSelectedUserForReset(user)}
                              className="flex items-center space-x-1 px-3 py-1 bg-pastel-blue-400 text-white rounded-md hover:bg-pastel-blue-500 text-sm"
                            >
                              <Key className="w-4 h-4" />
                              <span>Reset Password</span>
                            </button>
                            <button
                              onClick={() => setSelectedUser(user)}
                              className="flex items-center space-x-1 px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-400 text-sm"
                            >
                              <UserX className="w-4 h-4" />
                              <span>Delete User</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : activeTab === 'vendors' ? (
            <VendorManagement />
          ) : activeTab === 'venues' ? (
            <VenueManagement />
          ) : activeTab === 'withdrawals' ? (
            <WithdrawalManagement />
          ) : (
            // Fee Structure Tab
            <div className="card-pastel p-6 space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800 font-poppins">Transaction Fee Structure</h2>
                <button
                  onClick={() => setShowFeeSettingsModal(true)}
                  className="btn-pastel flex items-center space-x-2 px-4 py-2"
                >
                  <Settings className="w-4 h-4" />
                  <span>Edit Fee Settings</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-pastel-blue-50 border border-pastel-blue-200 p-6 rounded-lg">
                  <div className="flex items-center space-x-3 mb-4">
                    <Building className="w-6 h-6 text-pastel-blue-600" />
                    <h3 className="text-lg font-medium text-pastel-blue-700 font-poppins">Platform Fee (Commission)</h3>
                  </div>
                  <p className="text-3xl font-bold text-pastel-blue-600 mb-2 font-poppins">{platformFeeRate.toFixed(2)}%</p>
                  <p className="text-pastel-blue-600">Our commission on each completed payment</p>
                  <div className="mt-4 pt-4 border-t border-pastel-blue-200">
                    <p className="text-pastel-blue-600 text-sm">
                      For LKR 10,000: <span className="font-medium text-pastel-blue-700">LKR {(10000 * (platformFeeRate / 100)).toFixed(2)}</span>
                    </p>
                  </div>
                </div>

                <div className="bg-pastel-purple-50 border border-pastel-purple-200 p-6 rounded-lg">
                  <div className="flex items-center space-x-3 mb-4">
                    <CreditCard className="w-6 h-6 text-pastel-purple-600" />
                    <h3 className="text-lg font-medium text-pastel-purple-700 font-poppins">Gateway Fee</h3>
                  </div>
                  <p className="text-3xl font-bold text-pastel-purple-600 mb-2 font-poppins">{gatewayFeeRate.toFixed(2)}%</p>
                  <p className="text-pastel-purple-600">PayHere payment processing fee</p>
                  <div className="mt-4 pt-4 border-t border-pastel-purple-200">
                    <p className="text-pastel-purple-600 text-sm">
                      For LKR 10,000: <span className="font-medium text-pastel-purple-700">LKR {(10000 * (gatewayFeeRate / 100)).toFixed(2)}</span>
                    </p>
                  </div>
                </div>

                <div className="bg-pastel-green-50 border border-pastel-green-200 p-6 rounded-lg">
                  <div className="flex items-center space-x-3 mb-4">
                    <Percent className="w-6 h-6 text-pastel-green-600" />
                    <h3 className="text-lg font-medium text-pastel-green-700 font-poppins">Total Fee</h3>
                  </div>
                  <p className="text-3xl font-bold text-pastel-green-600 mb-2 font-poppins">{(platformFeeRate + gatewayFeeRate).toFixed(2)}%</p>
                  <p className="text-pastel-green-600">Combined fee added on top of gift amount</p>
                  <div className="mt-4 pt-4 border-t border-pastel-green-200">
                    <p className="text-pastel-green-600 text-sm">
                      For LKR 10,000: <span className="font-medium text-pastel-green-700">LKR {(10000 * ((platformFeeRate + gatewayFeeRate) / 100)).toFixed(2)}</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/60 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 font-poppins">Example Transaction Breakdown — All Fee Models</h3>
                <p className="text-gray-500 text-sm mb-4">For a LKR 10,000 gift with current fee rates ({platformFeeRate.toFixed(2)}% platform + {gatewayFeeRate.toFixed(2)}% gateway = {(platformFeeRate + gatewayFeeRate).toFixed(2)}% total):</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Guest Pays */}
                  <div className="bg-pastel-blue-50 border border-pastel-blue-200 p-4 rounded-lg">
                    <h4 className="text-pastel-blue-600 font-medium mb-3 font-poppins">Guest Pays</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Gift Amount:</span>
                        <span className="text-gray-800 font-medium">LKR 10,000.00</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Guest pays fee:</span>
                        <span className="text-pastel-blue-600 font-medium">LKR {(10000 * ((platformFeeRate + gatewayFeeRate) / 100)).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Couple pays fee:</span>
                        <span className="text-gray-400">LKR 0.00</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-pastel-blue-200">
                        <span className="text-gray-800 font-medium">Guest pays:</span>
                        <span className="text-gray-800 font-bold">LKR {(10000 * (1 + (platformFeeRate + gatewayFeeRate) / 100)).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-800 font-medium">Couple receives:</span>
                        <span className="text-pastel-green-600 font-bold">LKR 10,000.00</span>
                      </div>
                    </div>
                  </div>

                  {/* Couple Absorbs */}
                  <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                    <h4 className="text-orange-600 font-medium mb-3 font-poppins">Couple Absorbs</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Gift Amount:</span>
                        <span className="text-gray-800 font-medium">LKR 10,000.00</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Guest pays fee:</span>
                        <span className="text-gray-400">LKR 0.00</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Couple pays fee:</span>
                        <span className="text-orange-600 font-medium">LKR {(10000 * ((platformFeeRate + gatewayFeeRate) / 100)).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-orange-200">
                        <span className="text-gray-800 font-medium">Guest pays:</span>
                        <span className="text-gray-800 font-bold">LKR 10,000.00</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-800 font-medium">Couple receives:</span>
                        <span className="text-pastel-green-600 font-bold">LKR {(10000 * (1 - (platformFeeRate + gatewayFeeRate) / 100)).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Split 50/50 */}
                  <div className="bg-pastel-green-50 border border-pastel-green-200 p-4 rounded-lg">
                    <h4 className="text-pastel-green-600 font-medium mb-3 font-poppins">Split 50/50</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Gift Amount:</span>
                        <span className="text-gray-800 font-medium">LKR 10,000.00</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Guest pays fee:</span>
                        <span className="text-pastel-blue-600 font-medium">LKR {(10000 * ((platformFeeRate + gatewayFeeRate) / 100) / 2).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Couple pays fee:</span>
                        <span className="text-orange-600 font-medium">LKR {(10000 * ((platformFeeRate + gatewayFeeRate) / 100) / 2).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-pastel-green-200">
                        <span className="text-gray-800 font-medium">Guest pays:</span>
                        <span className="text-gray-800 font-bold">LKR {(10000 * (1 + (platformFeeRate + gatewayFeeRate) / 200)).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-800 font-medium">Couple receives:</span>
                        <span className="text-pastel-green-600 font-bold">LKR {(10000 * (1 - (platformFeeRate + gatewayFeeRate) / 200)).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Real Transaction Summary */}
              {stats && stats.total_transactions > 0 && (
                <div className="bg-white/60 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 font-poppins">Actual Collected Fees Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white/60 p-4 rounded-lg">
                      <h4 className="text-pastel-blue-600 font-medium mb-2 font-poppins">Guest Pays Transactions</h4>
                      <p className="text-sm text-gray-500">{stats.guest_pays_count || 0} transactions</p>
                      <p className="text-lg font-bold text-pastel-blue-600 mt-1 font-poppins">LKR {(stats.commission_from_guest_pays || 0).toFixed(2)}</p>
                      <p className="text-xs text-gray-400 mt-1">All fees paid by guests on top of gifts</p>
                    </div>
                    <div className="bg-white/60 p-4 rounded-lg">
                      <h4 className="text-orange-600 font-medium mb-2 font-poppins">Couple Absorbs Transactions</h4>
                      <p className="text-sm text-gray-500">{stats.couple_absorbs_count || 0} transactions</p>
                      <p className="text-lg font-bold text-orange-600 mt-1 font-poppins">LKR {(stats.commission_from_couple_absorbs || 0).toFixed(2)}</p>
                      <p className="text-xs text-gray-400 mt-1">All fees deducted from couples' gifts</p>
                    </div>
                    <div className="bg-white/60 p-4 rounded-lg">
                      <h4 className="text-pastel-green-600 font-medium mb-2 font-poppins">Split 50/50 Transactions</h4>
                      <p className="text-sm text-gray-500">{stats.split_count || 0} transactions</p>
                      <p className="text-lg font-bold text-pastel-green-600 mt-1 font-poppins">LKR {(stats.commission_from_splits || 0).toFixed(2)}</p>
                      <p className="text-xs text-gray-400 mt-1">Half from guest, half from couple</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-pastel-purple-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex justify-between bg-white/60 p-3 rounded-lg">
                        <span className="text-gray-600">Total fees paid by guests:</span>
                        <span className="text-pastel-blue-600 font-bold">LKR {(stats.total_guest_fee_share || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between bg-white/60 p-3 rounded-lg">
                        <span className="text-gray-600">Total fees deducted from couples:</span>
                        <span className="text-orange-600 font-bold">LKR {(stats.total_couple_fee_share || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white/60 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 font-poppins">Fee Policy</h3>
                <p className="text-gray-600 mb-3">
                  All payments are processed via PayHere. Our platform charges a
                  total fee of {(platformFeeRate + gatewayFeeRate).toFixed(2)}% on all transactions.
                  Each registry owner chooses who pays this fee:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  <li><span className="text-pastel-blue-600 font-medium">Guest Pays</span> — The full fee is added on top of the gift amount. The couple receives the full gift.</li>
                  <li><span className="text-orange-600 font-medium">Couple Absorbs</span> — The guest pays only the gift amount. The fee is deducted from the couple's gift.</li>
                  <li><span className="text-pastel-green-600 font-medium">Split 50/50</span> — Half the fee is added on top of the gift (paid by guest), half is deducted from the gift (paid by couple).</li>
                </ul>
                <div className="mt-4 pt-4 border-t border-pastel-purple-200">
                  <p className="text-pastel-blue-600 text-sm">
                    <span className="font-medium">{platformFeeRate.toFixed(2)}% Platform Commission</span> — Our fee collected automatically per transaction
                  </p>
                  <p className="text-pastel-purple-600 text-sm mt-1">
                    <span className="font-medium">{gatewayFeeRate.toFixed(2)}% Gateway Fee</span> — PayHere processing cost
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedRegistry && (
        <DeleteRegistryModal
          registry={selectedRegistry}
          onClose={() => setSelectedRegistry(null)}
          onDelete={loadData}
        />
      )}

      {selectedUser && (
        <DeleteUserModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onDelete={loadData}
        />
      )}

      {selectedUserForReset && (
        <ResetUserPasswordModal
          user={selectedUserForReset}
          onClose={() => setSelectedUserForReset(null)}
          onSuccess={loadData}
        />
      )}

      {showFeeSettingsModal && (
        <FeeSettingsModal
          onClose={() => setShowFeeSettingsModal(false)}
          onUpdate={loadData}
        />
      )}

      {invoiceRegistry && (
        <InvoiceModal
          registryId={invoiceRegistry.registry_id}
          registryTitle={invoiceRegistry.title}
          onClose={() => setInvoiceRegistry(null)}
        />
      )}
    </div>
  );
}
